import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, Save, Plus, Pencil, Trash2, X, Sparkles, Info, ChevronRight, Target,
} from "lucide-react";
import CadenceEditor from "../components/CadenceEditor";
import ConfirmModal from "../components/ConfirmModal";
import ABReportPanel from "../components/ABReportPanel";

const BLANK = {
  name: "",
  product_description: "",
  target_niche: "",
  target_city: "",
  value_prop: "",
  deal_size_usd: 2500,
  icp: {
    website_qualities: [],
    rating_min: null,
    rating_max: null,
    reviews_min: null,
    reviews_max: null,
    match_niche: false,
    match_city: false,
  },
  sequence_steps: null,
  business_hours_start: null,
  business_hours_end: null,
  business_days_only: null,
  smart_scheduling: null,
  max_sends_per_hour: null,
  auto_book_demo: false,
  // A/B testing — off by default; when the user flips it on we prefill B with
  // a copy of A so they always have a diff-able starting point.
  ab_enabled: false,
  ab_split_ratio: 0.5,
  sequence_steps_variant_b: null,
};

const FIELD_HELP = {
  name: {
    hint: "Internal label for this campaign. You'll see it across the CRM (leads, ROI, Kanban).",
    example: "e.g. \"Wedding Venues — Texas\" · \"Med Spa Q2 Push\" · \"FL Photographers\"",
  },
  product_description: {
    hint: "What you're actually selling. Plain language. Claude uses this in every AI-generated call script & SMS.",
    example: "e.g. \"A modern booking-and-lead-capture website (like lopezhill.com) that lets venues capture inquiries 24/7 and stop drowning in DMs.\"",
  },
  target_niche: {
    hint: "Used by the scraper AND by ROI attribution. Be specific — match how Google Maps would list it.",
    example: "e.g. \"wedding venue\" · \"med spa\" · \"wedding photographer\" · \"hair salon\"",
  },
  target_city: {
    hint: "Optional but recommended — sharpens scraping and prevents cross-attribution between campaigns.",
    example: "e.g. \"Austin, TX\" · \"Miami, FL\" · leave empty to target many cities",
  },
  value_prop: {
    hint: "The concrete outcome you give buyers. AI weaves this into every pitch. Specifics > buzzwords.",
    example: "e.g. \"Save 10 hrs/week on email follow-ups + capture 30% more booked tours.\"",
  },
  deal_size_usd: {
    hint: "Average revenue PER closed deal. Used for ROI math (Revenue, Profit, CAC).",
    example: "e.g. 2500 (one-time site) · 297 (monthly subscription × 12 = 3564 LTV)",
  },
};

const AI_ENABLED_FIELDS = ["name", "product_description", "value_prop"];

// Used when user toggles OFF "use global cadence" for the first time —
// gives them a sane 5-touch starting point they can edit instead of starting
// from an empty list.
const DEFAULT_CADENCE_TEMPLATE = [
  { step: 1, day_offset: 0, hour: 10, minute: 30, kind: "sms",  angle: "intro" },
  { step: 2, day_offset: 1, hour: 14, minute: 0,  kind: "call", angle: "first_attempt" },
  { step: 3, day_offset: 3, hour: 11, minute: 0,  kind: "sms",  angle: "value" },
  { step: 4, day_offset: 6, hour: 15, minute: 30, kind: "call", angle: "second_attempt" },
  { step: 5, day_offset: 9, hour: 10, minute: 0,  kind: "sms",  angle: "breakup" },
];

export default function Campaign() {
  const [campaigns, setCampaigns] = useState([]);
  const [mode, setMode] = useState("list"); // 'list' | 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(null); // field key being improved
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm(BLANK);
    setEditingId(null);
    setMode("create");
  };

  const startEdit = (c) => {
    setForm({
      name: c.name || "",
      product_description: c.product_description || "",
      target_niche: c.target_niche || "",
      target_city: c.target_city || "",
      value_prop: c.value_prop || "",
      deal_size_usd: c.deal_size_usd ?? 2500,
      icp: {
        website_qualities: c.icp?.website_qualities || [],
        rating_min: c.icp?.rating_min ?? null,
        rating_max: c.icp?.rating_max ?? null,
        reviews_min: c.icp?.reviews_min ?? null,
        reviews_max: c.icp?.reviews_max ?? null,
        match_niche: c.icp?.match_niche || false,
        match_city: c.icp?.match_city || false,
      },
      sequence_steps: Array.isArray(c.sequence_steps) && c.sequence_steps.length ? c.sequence_steps : null,
      business_hours_start: c.business_hours_start ?? null,
      business_hours_end: c.business_hours_end ?? null,
      business_days_only: c.business_days_only ?? null,
      smart_scheduling: c.smart_scheduling ?? null,
      max_sends_per_hour: c.max_sends_per_hour ?? null,
      auto_book_demo: c.auto_book_demo || false,
      ab_enabled: !!c.ab_enabled,
      ab_split_ratio: typeof c.ab_split_ratio === "number" ? c.ab_split_ratio : 0.5,
      sequence_steps_variant_b: Array.isArray(c.sequence_steps_variant_b) && c.sequence_steps_variant_b.length
        ? c.sequence_steps_variant_b
        : null,
    });
    setEditingId(c.id);
    setMode("edit");
  };

  const cancel = () => {
    setMode("list");
    setEditingId(null);
    setForm(BLANK);
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      if (mode === "edit" && editingId) {
        await api.put(`/campaigns/${editingId}`, form);
        toast.success("Campaign updated");
      } else {
        await api.post("/campaigns", form);
        toast.success("Campaign created");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const doDelete = (c) => {
    setConfirmModal({
      title: `Delete campaign "${c.name}"?`,
      body: "Leads attributed to it will be detached (NOT deleted) — they'll just lose campaign attribution.",
      destructive: true,
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await api.delete(`/campaigns/${c.id}`);
          toast.success("Campaign deleted");
          await load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
      },
    });
  };

  const aiImprove = async (field) => {
    setAiLoading(field);
    try {
      const { data } = await api.post("/campaigns/ai-suggest", {
        field,
        current_value: form[field] || "",
        target_niche: form.target_niche || "",
        target_city: form.target_city || "",
        product_description: form.product_description || "",
      });
      setForm((s) => ({ ...s, [field]: data.suggestion }));
      toast.success("AI suggestion applied — edit further if you want");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI failed");
    } finally { setAiLoading(null); }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  if (loading) return <div className="p-8 text-sm text-zinc-500 font-mono">Loading…</div>;

  // FORM MODE
  if (mode === "create" || mode === "edit") {
    return (
      <div className="p-8 max-w-4xl" data-testid="campaign-form-page">
        <button
          onClick={cancel}
          data-testid="btn-back-to-list"
          className="text-xs font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 mb-6 inline-flex items-center gap-1"
        >
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        <div className="border-b border-zinc-200 pb-6 mb-6">
          <div className="overline mb-2">
            / {mode === "edit" ? "edit campaign" : "new campaign"}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">
            {mode === "edit" ? "Edit Campaign" : "New Campaign"}
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Tell the AI what you're selling and to whom. Every call script, SMS, and email will be tailored to this profile.
            Use the <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#002FA7]" /> AI Improve</span> button to refine any field.
          </p>
        </div>

        <form onSubmit={save} className="border border-zinc-200 bg-white p-6 space-y-6">
          <Field
            label="Campaign Name"
            help={FIELD_HELP.name}
            aiBtn={
              <AIButton
                onClick={() => aiImprove("name")}
                loading={aiLoading === "name"}
                disabled={!form.target_niche && !form.product_description}
                title={!form.target_niche ? "Fill Target Niche first for better suggestions" : ""}
              />
            }
          >
            <input
              required value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="camp-name"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
              placeholder="Wedding Venues — Texas Push"
            />
          </Field>

          <Field
            label="Product Description"
            help={FIELD_HELP.product_description}
            aiBtn={
              <AIButton
                onClick={() => aiImprove("product_description")}
                loading={aiLoading === "product_description"}
              />
            }
          >
            <textarea
              required value={form.product_description}
              onChange={(e) => set("product_description", e.target.value)}
              data-testid="camp-product"
              rows={3}
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
              placeholder="A modern booking & lead-capture website for wedding venues — like lopezhill.com"
            />
          </Field>

          <div className="grid grid-cols-2 gap-5">
            <Field label="Target Niche" help={FIELD_HELP.target_niche}>
              <input
                required value={form.target_niche} onChange={(e) => set("target_niche", e.target.value)}
                data-testid="camp-niche"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                placeholder="wedding venue"
              />
            </Field>
            <Field label="Target City (optional)" help={FIELD_HELP.target_city}>
              <input
                value={form.target_city} onChange={(e) => set("target_city", e.target.value)}
                data-testid="camp-city"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                placeholder="Austin, TX"
              />
            </Field>
          </div>

          <Field
            label="Value Proposition"
            help={FIELD_HELP.value_prop}
            aiBtn={
              <AIButton
                onClick={() => aiImprove("value_prop")}
                loading={aiLoading === "value_prop"}
              />
            }
          >
            <textarea
              required value={form.value_prop} onChange={(e) => set("value_prop", e.target.value)}
              data-testid="camp-vp"
              rows={3}
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
              placeholder="Save 10 hrs/week on follow-ups, capture 30% more inquiries, look premium online"
            />
          </Field>

          <Field label="Deal Size (USD)" help={FIELD_HELP.deal_size_usd}>
            <input
              type="number" min={0} step={50}
              value={form.deal_size_usd}
              onChange={(e) => set("deal_size_usd", Number(e.target.value))}
              data-testid="camp-deal-size"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono"
              placeholder="2500"
            />
          </Field>

          {/* ICP Section */}
          <div className="border-t border-zinc-200 pt-6" data-testid="icp-section">
            <div className="mb-4">
              <div className="overline text-[#002FA7]">/ ideal customer profile (icp)</div>
              <p className="text-xs text-zinc-600 mt-1.5 max-w-xl">
                Rules used by the <strong>ICP Only</strong> toggle on the Leads page to surface only your best-fit leads.
                Leave fields blank to ignore them.
              </p>
            </div>

            <div className="space-y-5 bg-zinc-50/60 border border-zinc-200 p-5">
              {/* Website quality multi-select */}
              <div>
                <label className="overline block mb-2">Website Quality (match any)</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { v: "none", label: "No website", hint: "Highest opportunity" },
                    { v: "bad", label: "Bad website", hint: "Outdated / unprofessional" },
                    { v: "good", label: "Good website", hint: "Targets with budget" },
                  ].map((o) => {
                    const active = form.icp.website_qualities.includes(o.v);
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => {
                          const cur = form.icp.website_qualities;
                          const next = active ? cur.filter((x) => x !== o.v) : [...cur, o.v];
                          setForm((s) => ({ ...s, icp: { ...s.icp, website_qualities: next } }));
                        }}
                        data-testid={`icp-wq-${o.v}`}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition ${
                          active
                            ? "bg-[#002FA7] border-[#002FA7] text-white"
                            : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
                        }`}
                        title={o.hint}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-zinc-400 mt-1.5 font-mono">e.g. select "No website" + "Bad website" to target venues that need yours.</p>
              </div>

              {/* Rating */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1.5">Min Rating (1–5)</label>
                  <input
                    type="number" min={1} max={5} step={0.1}
                    value={form.icp.rating_min ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, icp: { ...s.icp, rating_min: e.target.value ? Number(e.target.value) : null } }))}
                    data-testid="icp-rating-min"
                    className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7]"
                    placeholder="3.5"
                  />
                </div>
                <div>
                  <label className="overline block mb-1.5">Max Rating (1–5)</label>
                  <input
                    type="number" min={1} max={5} step={0.1}
                    value={form.icp.rating_max ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, icp: { ...s.icp, rating_max: e.target.value ? Number(e.target.value) : null } }))}
                    data-testid="icp-rating-max"
                    className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7]"
                    placeholder="4.5"
                  />
                </div>
              </div>

              {/* Reviews */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1.5">Min Reviews</label>
                  <input
                    type="number" min={0}
                    value={form.icp.reviews_min ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, icp: { ...s.icp, reviews_min: e.target.value ? Number(e.target.value) : null } }))}
                    data-testid="icp-reviews-min"
                    className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7]"
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="overline block mb-1.5">Max Reviews</label>
                  <input
                    type="number" min={0}
                    value={form.icp.reviews_max ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, icp: { ...s.icp, reviews_max: e.target.value ? Number(e.target.value) : null } }))}
                    data-testid="icp-reviews-max"
                    className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:border-[#002FA7]"
                    placeholder="500"
                  />
                </div>
              </div>
              <p className="text-xs text-zinc-500 -mt-2">
                <Info className="w-3 h-3 inline mr-1 text-zinc-400" />
                Use rating/review bounds to target underperformers (3.5–4.2★ with 10+ reviews = real businesses missing the basics).
              </p>

              {/* Toggles */}
              <div className="space-y-2 pt-2 border-t border-zinc-200">
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox" checked={form.icp.match_niche}
                    onChange={(e) => setForm((s) => ({ ...s, icp: { ...s.icp, match_niche: e.target.checked } }))}
                    data-testid="icp-match-niche"
                    className="mt-0.5"
                  />
                  <span>
                    Require lead's niche to match <span className="font-mono text-xs bg-zinc-200 px-1">{form.target_niche || "(target niche)"}</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox" checked={form.icp.match_city}
                    onChange={(e) => setForm((s) => ({ ...s, icp: { ...s.icp, match_city: e.target.checked } }))}
                    data-testid="icp-match-city"
                    className="mt-0.5"
                  />
                  <span>
                    Require lead's city to match <span className="font-mono text-xs bg-zinc-200 px-1">{form.target_city || "(target city)"}</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Per-Campaign Cadence — overrides global Settings */}
          <div className="border border-zinc-200 bg-zinc-50/40 p-5" data-testid="campaign-cadence-block">
            <div className="mb-4">
              <div className="overline mb-1">/ outreach cadence (this campaign)</div>
              <h3 className="font-heading text-lg font-bold">Touchpoint Sequence</h3>
              <p className="text-xs text-zinc-600 mt-1">
                Override the global cadence for this campaign. Useful when one industry needs 7 SMS + 3 calls (wedding venues)
                and another only needs 3 calls (restaurants). Leave "Use global" checked to inherit your Settings default.
              </p>
            </div>
            <CadenceEditor
              steps={form.sequence_steps || []}
              useGlobal={!form.sequence_steps}
              onUseGlobalChange={(v) =>
                setForm((s) => ({
                  ...s,
                  sequence_steps: v
                    ? null
                    : (s.sequence_steps && s.sequence_steps.length
                        ? s.sequence_steps
                        : DEFAULT_CADENCE_TEMPLATE),
                }))
              }
              onStepsChange={(next) => setForm((s) => ({ ...s, sequence_steps: next }))}
              businessHoursStart={form.business_hours_start ?? 9}
              businessHoursEnd={form.business_hours_end ?? 18}
              businessDaysOnly={form.business_days_only ?? true}
              smartScheduling={form.smart_scheduling ?? false}
              onHoursChange={({ key, value }) => setForm((s) => ({ ...s, [key]: value }))}
              campaignId={editingId}
              testidPrefix="camp-cadence"
            />

            {/* Pacing throttle — protects Twilio number from carrier spam flags */}
            <div className="mt-5 pt-5 border-t border-zinc-200">
              <label className="overline block mb-2">/ pacing (anti-spam)</label>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="number" min={0} max={500}
                  value={form.max_sends_per_hour ?? ""}
                  placeholder="30"
                  onChange={(e) => setForm((s) => ({
                    ...s,
                    max_sends_per_hour: e.target.value === "" ? null : Number(e.target.value),
                  }))}
                  data-testid="camp-pacing-input"
                  className="w-24 px-3 py-2 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]"
                />
                <div className="text-xs text-zinc-600">
                  Max <strong>outbound SMS + calls / hour</strong> this campaign will fire.
                  Default <span className="font-mono">30</span> — leaves headroom on a single Twilio number
                  and avoids carrier spam flags. Excess touches are slid forward, not dropped.
                </div>
              </div>
            </div>

            {/* A/B TEST — run two cadences head-to-head against the same ICP */}
            <div className="mt-5 pt-5 border-t border-zinc-200" data-testid="camp-ab-block">
              <label className="overline block mb-2">/ a/b test cadence</label>
              <div className="flex items-start gap-3 mb-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!form.ab_enabled}
                  onClick={() => setForm((s) => {
                    // Toggling ON: prefill variant B with a copy of A so users
                    // have something to diff. Toggling OFF: don't destroy B — user
                    // may re-enable later; server-side promote is the "clear" path.
                    const enabling = !s.ab_enabled;
                    return {
                      ...s,
                      ab_enabled: enabling,
                      sequence_steps_variant_b: (
                        enabling && !s.sequence_steps_variant_b
                          ? (s.sequence_steps && s.sequence_steps.length
                              ? JSON.parse(JSON.stringify(s.sequence_steps))
                              : DEFAULT_CADENCE_TEMPLATE.map(x => ({ ...x })))
                          : s.sequence_steps_variant_b
                      ),
                    };
                  })}
                  data-testid="camp-ab-toggle"
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center transition-colors ${
                    form.ab_enabled ? "bg-[#002FA7]" : "bg-zinc-300"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform bg-white transition-transform ${
                      form.ab_enabled ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
                <div className="text-xs text-zinc-600 leading-relaxed flex-1">
                  Splits <strong>incoming leads</strong> into two cadences — Variant A (the one above) vs.
                  Variant B (edit below). Assignment is deterministic on lead id, sticky forever, and 50/50
                  by default. Reports live under the <em>Campaign detail → A/B</em> tab.
                </div>
              </div>

              {form.ab_enabled && (
                <>
                  {/* Split ratio slider */}
                  <div className="mb-4">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-2">
                      Split ratio · <span className="text-[#002FA7]">{Math.round((form.ab_split_ratio ?? 0.5) * 100)}% A</span> /{" "}
                      <span className="text-amber-700">{100 - Math.round((form.ab_split_ratio ?? 0.5) * 100)}% B</span>
                    </label>
                    <input
                      type="range"
                      min={10} max={90} step={5}
                      value={Math.round((form.ab_split_ratio ?? 0.5) * 100)}
                      onChange={(e) => setForm((s) => ({ ...s, ab_split_ratio: Number(e.target.value) / 100 }))}
                      data-testid="camp-ab-split-slider"
                      className="w-full max-w-md accent-[#002FA7]"
                    />
                    <div className="text-[10px] text-zinc-500 mt-1 font-mono max-w-md">
                      50/50 is standard. Push toward A only when B is unproven / risky (e.g. 80/20 gate).
                    </div>
                  </div>

                  {/* Variant B cadence editor */}
                  <div className="border border-amber-300 bg-amber-50/40 p-4" data-testid="camp-variant-b-block">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
                          Variant B · challenger
                        </div>
                        <p className="text-xs text-zinc-600 mt-0.5">
                          Same lead pool, different cadence. Tweak steps, timing, or channel mix — the AI
                          will honor the angles you set on each touchpoint.
                        </p>
                      </div>
                    </div>
                    <CadenceEditor
                      steps={form.sequence_steps_variant_b || []}
                      useGlobal={false}
                      showUseGlobalToggle={false}
                      onStepsChange={(next) => setForm((s) => ({ ...s, sequence_steps_variant_b: next }))}
                      businessHoursStart={form.business_hours_start ?? 9}
                      businessHoursEnd={form.business_hours_end ?? 18}
                      businessDaysOnly={form.business_days_only ?? true}
                      smartScheduling={form.smart_scheduling ?? false}
                      onHoursChange={({ key, value }) => setForm((s) => ({ ...s, [key]: value }))}
                      campaignId={editingId}
                      testidPrefix="camp-cadence-b"
                    />
                  </div>

                  {/* Live scoreboard — only meaningful for saved campaigns
                      (needs a campaign_id to query). */}
                  {editingId && mode === "edit" && (
                    <ABReportPanel
                      campaignId={editingId}
                      onPromoted={(v) => {
                        // Backend clears variant B + turns A/B off. Reflect that
                        // locally so the form and UI update immediately.
                        setForm((s) => ({
                          ...s,
                          ab_enabled: false,
                          sequence_steps_variant_b: null,
                        }));
                        toast.success(`Variant ${v} is now the primary cadence.`);
                      }}
                    />
                  )}
                </>
              )}
            </div>

            {/* Demo-Auto-Book — closes "interested" replies on autopilot */}
            <div className="mt-5 pt-5 border-t border-zinc-200" data-testid="camp-autobook-block">
              <label className="overline block mb-2">/ demo-auto-book</label>
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!form.auto_book_demo}
                  onClick={() => setForm((s) => ({ ...s, auto_book_demo: !s.auto_book_demo }))}
                  data-testid="camp-autobook-toggle"
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center transition-colors ${
                    form.auto_book_demo ? "bg-[#002FA7]" : "bg-zinc-300"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform bg-white transition-transform ${
                      form.auto_book_demo ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
                <div className="text-xs text-zinc-600 leading-relaxed flex-1">
                  When ON, an inbound SMS or call transcript classified as <strong>interested</strong>{" "}
                  (≥60% confidence) automatically generates a one-shot booking link and texts it to
                  the lead. They self-serve a slot from your connected Google Calendar — no SDR
                  hop needed. Requires Google Calendar connected in Settings.
                  <div className="mt-3">
                    <a
                      href="/book/preview"
                      target="_blank"
                      rel="noreferrer"
                      data-testid="camp-preview-booking-link"
                      className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-3 py-1.5 transition-colors"
                    >
                      Preview booking page →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-3 border-t border-zinc-100">
            <button
              type="submit" disabled={saving}
              data-testid="camp-save"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {mode === "edit" ? "Save Changes" : "Create Campaign"}
            </button>
            <button
              type="button" onClick={cancel}
              data-testid="camp-cancel"
              className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-4 py-2.5 text-xs font-bold uppercase tracking-widest"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // LIST MODE
  return (
    <div className="p-8 max-w-5xl" data-testid="campaign-page">
      <div className="border-b border-zinc-200 pb-6 mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-2">/ campaigns</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Your Campaigns</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Each campaign is a separate pitch: product + niche + value prop. Used by the AI for personalization
            and by the ROI dashboard for attribution.
          </p>
        </div>
        <button
          onClick={startCreate}
          data-testid="btn-new-campaign"
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 shrink-0"
        >
          <Plus className="w-3.5 h-3.5" /> New Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-12 text-center">
          <Target className="w-8 h-8 mx-auto text-zinc-400 mb-3" />
          <div className="font-heading text-lg font-bold mb-1">No campaigns yet</div>
          <p className="text-sm text-zinc-500 mb-5">Create your first campaign to start scraping and pitching leads.</p>
          <button
            onClick={startCreate}
            data-testid="btn-new-campaign-empty"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> Create Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="campaign-list">
          {campaigns.map((c) => (
            <div
              key={c.id}
              data-testid={`campaign-row-${c.id}`}
              className="border border-zinc-200 bg-white p-5 hover:border-[#002FA7] transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-heading text-lg font-bold truncate">{c.name}</h3>
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-zinc-900 text-white px-1.5 py-0.5">
                      ${(c.deal_size_usd ?? 2500).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs font-mono text-zinc-500 mb-3">
                    {c.target_niche} · {c.target_city || "any city"}
                  </div>
                  <div className="text-sm text-zinc-700 mb-2 line-clamp-2">
                    <span className="font-semibold text-zinc-500 uppercase text-[10px] tracking-wider mr-2">Product</span>
                    {c.product_description}
                  </div>
                  <div className="text-sm text-zinc-700 line-clamp-2">
                    <span className="font-semibold text-zinc-500 uppercase text-[10px] tracking-wider mr-2">Pitch</span>
                    {c.value_prop}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => startEdit(c)}
                    data-testid={`camp-edit-${c.id}`}
                    className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => doDelete(c)}
                    data-testid={`camp-delete-${c.id}`}
                    className="border border-red-300 hover:bg-red-50 text-red-600 px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-100 text-[10px] uppercase tracking-wider font-mono text-zinc-400">
                <span>Created {new Date(c.created_at).toLocaleDateString()}</span>
                {c.updated_at && <span>Updated {new Date(c.updated_at).toLocaleDateString()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  );
}

function Field({ label, help, aiBtn, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="overline">{label}</label>
        {aiBtn}
      </div>
      {children}
      {help && (
        <div className="mt-1.5 space-y-1">
          <p className="text-xs text-zinc-600 flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0 text-zinc-400" />
            {help.hint}
          </p>
          {help.example && (
            <p className="text-xs text-zinc-400 font-mono pl-4 leading-relaxed">{help.example}</p>
          )}
        </div>
      )}
    </div>
  );
}

function AIButton({ onClick, loading, disabled, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || disabled}
      data-testid="btn-ai-improve"
      title={title || "Use Claude to refine this field"}
      className="text-[10px] uppercase font-bold tracking-wider text-[#002FA7] hover:text-[#00227A] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      AI Improve
      <ChevronRight className="w-3 h-3" />
    </button>
  );
}
